import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { 
  Mail, 
  Shield, 
  X, 
  Edit3, 
  Trash2, 
  Search
} from 'lucide-react';

const UserListScreen = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getConfig } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users`, getConfig());
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  }, [getConfig]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_URL}/users/${id}`, getConfig());
        fetchUsers();
      } catch (error) {
        alert(error.response?.data?.message || 'Error deleting user');
      }
    }
  };



  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">User Management</h1>
          <p className="text-secondary-500 font-medium">View and manage registered users and their permissions.</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-3xl border border-secondary-100 flex items-center justify-between shadow-sm">
        <div className="relative w-96">
          <Search className="absolute left-4 top-3 text-secondary-400 h-5 w-5" />
          <input 
            type="text" 
            placeholder="Search users by name or email..." 
            className="w-full bg-secondary-50 border-none rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
          />
        </div>
        <p className="text-sm font-black text-secondary-400 uppercase tracking-widest px-6">
          Total Users: <span className="text-secondary-900">{users.length}</span>
        </p>
      </div>

      <div className="bg-white rounded-[40px] border border-secondary-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-secondary-50/50">
            <tr className="border-b border-secondary-100">
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">ID</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Name</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Email</th>
              <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest text-center">Admin Status</th>
              <th className="px-8 py-5 text-right font-black text-secondary-400 text-[10px] uppercase tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-50">
            {loading ? (
              <tr><td colSpan="5" className="text-center py-20 font-bold text-secondary-400">Loading users...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-20 font-bold text-secondary-400 uppercase tracking-widest">No users found</td></tr>
            ) : users.map((user) => (
              <tr key={user._id} className="hover:bg-secondary-50/50 transition-colors group">
                <td className="px-8 py-5 font-bold text-secondary-400 text-xs">#{user._id}</td>
                <td className="px-8 py-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-2xl flex items-center justify-center text-primary-700 font-black text-sm shadow-sm">
                      {user.name.charAt(0)}
                    </div>
                    <span className="font-bold text-secondary-900">{user.name}</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-secondary-500 font-medium">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 opacity-50" />
                    <span>{user.email}</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-center">
                  {user.isAdmin ? (
                    <span className="inline-flex items-center space-x-2 px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-[10px] font-black uppercase tracking-widest">
                      <Shield className="h-3 w-3" />
                      <span>Admin Access</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center space-x-2 px-4 py-1.5 bg-secondary-100 text-secondary-500 rounded-full text-[10px] font-black uppercase tracking-widest">
                      <X className="h-3 w-3 opacity-50" />
                      <span>Customer</span>
                    </span>
                  )}
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all">
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(user._id)}
                      className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserListScreen;

